// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
import React from 'react';
import PropTypes from 'prop-types';

import trim from 'app/shared/helpers/trim';
import { CONSENT_MODES } from '@ion/api/consent-modes/modes';
import { useConsentModeEnabled } from 'app/components/consent-mode-select/GatedConsentModeSelect';
import {
  PipelineVariablesForm,
  RevisionSelect,
  AddIntegrationFields,
  ConsentCategorySelect,
  ConsentCategoryModeSelect,
} from './components';
import { MrH, MrLink, MrForm, useForm, MrButton, generateId, MrButtonList, MrSection } from '@ion/components';

import { CONSENT_FORM_TYPES } from './CONSENT_FORM_TYPES';

export default function PipelineIntegrationForm({
  pipeline,
  availableIntegrations,
  onSubmit,
  type,
  onCancel,
  selectedIntegration,
}) {
  const { consentModeEnabled } = useConsentModeEnabled();

  let initialFormState = {};
  let integration = null;
  let pipelineVariables = null;
  let consentCategory = null;
  let consentMode = consentModeEnabled ? CONSENT_MODES.ace : null;

  if (selectedIntegration) {
    integration = selectedIntegration.integration;
    pipelineVariables = selectedIntegration.mergedPipelineVars;
    consentCategory = selectedIntegration.consentCategory;
    consentMode = selectedIntegration.consentMode;

    Object.keys(pipelineVariables).forEach(variableName => {
      initialFormState[`pipeline-variable-${variableName}`] = { value: pipelineVariables[variableName], errorMsg: '' };
    });
  }
  initialFormState = {
    ...initialFormState,
    integration: { value: integration ? integration.id : '', errorMsg: '' },
    revision: { value: integration ? selectedIntegration.playbookId : '', errorMsg: '' },
    consentCategory: { value: consentCategory ? consentCategory.id : '', errorMsg: '' },
    // editing a legacy pipeline w/o consent defaults to '', forcing the user to make a choice
    consentMode: { value: consentMode ?? '', errorMsg: '' },
    disabled: {
      value: selectedIntegration?.pipelineIntegration ? selectedIntegration.pipelineIntegration.disabled : false,
      errorMsg: '',
    },
  };

  const formId = generateId();
  const { formState, validateField, setField, submitForm, resetError, removeFields } = useForm(
    formId,
    initialFormState
  );

  const isAddIntegration = type === CONSENT_FORM_TYPES.add;
  const isRevisionSelected = formState.revision.value !== '';

  const onSubmitForm = () => {
    const { data, isValid } = submitForm();
    if (isValid) {
      const pipelineVars = {};
      Object.keys(data).forEach(field => {
        if (field.startsWith('pipeline-variable')) {
          const pipelineVariableName = field.split('-')[2];
          pipelineVars[pipelineVariableName] = trim(data[field]);
        }
      });

      const disabled = data.disabled === 'true'; // necessary for mixing MUI checkbox and legacy form

      onSubmit({ ...data, pipelineVars: pipelineVars, disabled });
    }
  };

  const onChangeRevision = event => {
    setField(event);
  };

  return (
    <>
      <div className="header">
        <MrH h="h2" sans>
          {`${isAddIntegration ? 'Add New Integration' : `${selectedIntegration.integration.name}`} `}
        </MrH>
      </div>
      <div className="content">
        <MrForm onSubmit={onSubmitForm} id={formId}>
          <MrSection>
            {isAddIntegration && (
              <AddIntegrationFields
                setField={setField}
                formState={formState}
                validateField={validateField}
                pipeline={pipeline}
                availableIntegrations={availableIntegrations}
                removeFields={removeFields}
              />
            )}
            <RevisionSelect
              formState={formState}
              onChange={onChangeRevision}
              validateField={validateField}
              integrationId={formState.integration.value}
            />
            <ConsentCategoryModeSelect formState={formState} setField={setField} menuPosition="fixed" />
            <ConsentCategorySelect
              formState={formState}
              setField={setField}
              consentMode={formState.consentMode.value}
              menuPosition="fixed"
            />
          </MrSection>
          {isRevisionSelected && (
            <PipelineVariablesForm
              formState={formState}
              resetError={resetError}
              setField={setField}
              revisionId={formState.revision.value}
              integrationId={formState.integration.value}
            />
          )}
          <MrButtonList
            styleNames="marginTop"
            buttons={[
              <MrButton
                testId="addPipelineIntegration"
                text={isAddIntegration ? 'Add Integration' : 'Save'}
                key="create"
              />,
              <MrLink onClick={onCancel} styleNames="gray" key="cancel">
                Cancel
              </MrLink>,
            ]}
          />
        </MrForm>
      </div>
    </>
  );
}

PipelineIntegrationForm.propTypes = {
  pipeline: PropTypes.object.isRequired,
  availableIntegrations: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  selectedIntegration: PropTypes.object,
};
